import { useEffect, useCallback } from "react"
import type { DbObject } from "@/db/schema"
import { db } from "@/db/schema"
import { moveToTrash } from "@/db/operations"
import { toDbDate } from "@/shared/utils/date/dateUtils"
import dayjs from "dayjs"

/**
 * Custom events dispatched by task-detail shortcuts.
 * Child components (DetailDates, DetailActions) listen for these.
 */
export const TASK_DETAIL_EVENTS = {
  schedule: "task-detail:schedule",
  move: "task-detail:move",
} as const

/**
 * Keyboard shortcuts active when a task detail is open (P1 context).
 *
 * Uses capture phase so these fire BEFORE global useKeyboardShortcuts,
 * preventing conflicts (e.g. Mod+S = schedule here vs close in global).
 *
 * Shortcuts (per dreambase/ui/shortcuts.md Level 3):
 * - Mod+S         → Open schedule (WhenSheet)
 * - Mod+L         → Toggle flag (deadline)
 * - Mod+Shift+M   → Open container picker (Move)
 * - Mod+Backspace  → Delete task (move to trash)
 */
export function useTaskDetailShortcuts(
  task: DbObject | null,
  onClose: () => void,
) {
  const handleDelete = useCallback(async () => {
    if (!task?.id) return
    const taskId = task.id
    await moveToTrash(taskId)
    // Undo toast is auto-shown by registerUndo() in deleteTask
    onClose()
  }, [task?.id, onClose])

  const handleToggleFlag = useCallback(async () => {
    if (!task?.id) return
    // Toggle: if dueAt is set, remove it; otherwise set to today
    const newDueAt = task.dueAt ? undefined : toDbDate(dayjs())
    await db.totodo.update(task.id, { dueAt: newDueAt })
  }, [task?.id, task?.dueAt])

  useEffect(() => {
    if (!task) return

    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey
      if (!mod) return // All task-detail shortcuts require Mod

      const shift = e.shiftKey

      switch (e.key.toLowerCase()) {
        case "s":
          if (shift) return // Mod+Shift+S not used here
          e.preventDefault()
          e.stopImmediatePropagation() // Prevent global Mod+S from closing
          document.dispatchEvent(new CustomEvent(TASK_DETAIL_EVENTS.schedule))
          break

        case "l":
          if (shift) return
          e.preventDefault()
          e.stopImmediatePropagation()
          handleToggleFlag()
          break

        case "m":
          if (!shift) return // Only Mod+Shift+M
          e.preventDefault()
          e.stopImmediatePropagation()
          document.dispatchEvent(new CustomEvent(TASK_DETAIL_EVENTS.move))
          break

        case "backspace":
          if (shift) return
          // Don't intercept when editing text
          {
            const active = document.activeElement
            if (
              active instanceof HTMLElement &&
              (active.isContentEditable ||
                active.tagName === "INPUT" ||
                active.tagName === "TEXTAREA")
            ) {
              return
            }
          }
          e.preventDefault()
          e.stopImmediatePropagation()
          handleDelete()
          break
      }
    }

    // Capture phase → fires before global handler
    document.addEventListener("keydown", handler, true)
    return () => document.removeEventListener("keydown", handler, true)
  }, [task, handleDelete, handleToggleFlag])
}
